import assetUtils, { Asset, isNativeTokenForNetwork } from 'app-lib/assetUtils'
import errorUtils from 'app-lib/errorUtils'
import loanUtils from 'app-lib/loanUtils'
import numberUtils, { formatPercent } from 'app-lib/numberUtils'
import providerUtils from 'app-lib/providerUtils'
import appConfig, { chainConfigs } from 'bzx-common/src/config/appConfig'
import BorrowRequest from 'bzx-common/src/domain/borrow/BorrowRequest'
import ExpectedResult from 'bzx-common/src/shared-components/ExpectedResult'
import { RootStore } from 'bzx-common/src/stores'
import _ from 'lodash'
import React, { Component, FormEvent } from 'react'
import AssetDropdown from 'shared-components/AssetDropdown'
import { Button, Form, Icon, InfoTip, Input, Slider } from 'ui-framework'
import { BigNumber } from '@0x/utils'
import fulcrumProvider from '../../../services/FulcrumProvider'

export interface IBorrowFormProps {
  borrowAsset: Asset
  interestRate: BigNumber
  liquidity: BigNumber
  onSubmit: (request: BorrowRequest) => void
  onCancel: () => void
  rootStore: RootStore
}

interface IBorrowFormState {
  userBalances: { token: Asset; amount: BigNumber }[]
  borrowAmount: BigNumber
  collateralAsset: Asset
  borrowAmountValue: string
  depositAmount: BigNumber
  depositAmountValue: string
  balanceValue: BigNumber
  isLoading: boolean
  minValue: BigNumber
  maxValue: BigNumber
  sliderValue: number
  collaterizationPercents: BigNumber
  maxAvailableLiquidity: BigNumber
  liquidationPrice: BigNumber
  maintenanceMargin: BigNumber
  needsApproval: boolean
  itokenAddress: string
  nextBorrowInterestRate: BigNumber | null
}

export default class BorrowForm extends Component<IBorrowFormProps, IBorrowFormState> {
  private _borrowAmountChange = _.debounce(
    (value) => this.borrowAmountChange(value),
    appConfig.delay
  )
  private _depositAmountChange = _.debounce(
    (value) => this.depositAmountChange(value),
    appConfig.delay
  )
  private _sliderChange = _.debounce(
    () => this.depositAmountChange(this.state.depositAmountValue),
    appConfig.delay
  )

  public gasAmountNeeded = new BigNumber(3000000)
  private collateralAssets: Asset[]
  public _borrowDecimals = assetUtils.getDecimals(
    fulcrumProvider.currentChainId,
    this.props.borrowAsset
  )
  public constructor(props: IBorrowFormProps) {
    super(props)
    const chainId = fulcrumProvider.currentChainId
    const { borrowAsset } = this.props
    const chain = chainConfigs[chainId]
    this.collateralAssets = chain.borrowing.collateralAssets.filter(
      (asset) => asset !== borrowAsset
    )

    this.state = {
      userBalances: this.collateralAssets.map((asset) => ({
        token: asset,
        amount: new BigNumber(0),
      })),
      borrowAmount: new BigNumber(0),
      collateralAsset:
        chain.native.asset !== borrowAsset ? chain.native.asset : chain.stableCoins[0],
      borrowAmountValue: '',
      depositAmount: new BigNumber(0),
      depositAmountValue: '',
      balanceValue: new BigNumber(0),
      maxAvailableLiquidity: new BigNumber(0),
      isLoading: true,
      minValue: new BigNumber(120.3019),
      maxValue: new BigNumber(3000),
      sliderValue: 0,
      collaterizationPercents: new BigNumber(0),
      liquidationPrice: new BigNumber(0),
      maintenanceMargin: new BigNumber(0),
      needsApproval: false,
      nextBorrowInterestRate: null,
      itokenAddress: assetUtils.getITokenAddress(fulcrumProvider.currentChainId, borrowAsset),
    }
  }

  async refreshBorrowRate(borrowAmount: BigNumber) {
    try {
      const nextBorrowInterestRate = await loanUtils.nextBorrowInterestRate(
        fulcrumProvider,
        this.props.borrowAsset,
        borrowAmount
      )
      this.setState({ nextBorrowInterestRate })
    } catch (err) {
      // fail silently for now; TODO: discuss how to handle error on the UI
      console.error(err)
    }
  }

  private async setInputDefaults() {
    const { collateralAsset } = this.state
    const { borrowAsset } = this.props
    const balanceValue = await providerUtils.getTokenBalanceOfUser(fulcrumProvider, collateralAsset)
    const { maintenanceMargin, minInitialMargin } = await loanUtils.getLoanParams(
      fulcrumProvider,
      this.props.borrowAsset,
      collateralAsset,
      true
    )
    const { loanService } = this.props.rootStore.services
    const liquidity = await loanService.getLiquidityFromBlockchain([borrowAsset])
    const maxAvailableLiquidity = new BigNumber(liquidity[borrowAsset.toLowerCase()]).times(0.95)

    const minValue = minInitialMargin
      .div(10 ** 18)
      .plus(100)
      .plus(0.3019)

    this.setState(
      {
        maxAvailableLiquidity,
        maintenanceMargin: maintenanceMargin.div(10 ** 20),
        balanceValue,
        minValue,
        sliderValue: this.inverseCurve(minValue),
        collaterizationPercents: minValue,
        borrowAmountValue: '0',
        borrowAmount: new BigNumber(0),
      },
      () => this.setLiquidationPrice()
    )
  }

  private setNeedsApproval() {
    const { spendingApprovals } = this.props.rootStore

    const needsApproval = spendingApprovals.needsApproval(
      fulcrumProvider.currentChainId,
      this.state.collateralAsset,
      this.state.itokenAddress,
      this.state.depositAmount
    )
    this.setState({ needsApproval })
  }

  public async componentDidMount() {
    const { spendingApprovals, userWallet } = this.props.rootStore
    await this.setInputDefaults()
    await this.setMaxBorrow()
    await spendingApprovals.checkApprovals(this.collateralAssets, this.state.itokenAddress)
    const userBalances = await userWallet.getBalances(this.collateralAssets)
    this.setState({ userBalances, isLoading: false })
  }

  // custom logarithmic-like scale for slider
  // based on https://codesandbox.io/s/qxm182k0mw
  private sliderCurve(x: number): BigNumber {
    const min = this.state.minValue.toNumber() - 0.3019
    return new BigNumber(0.30194 * Math.exp(2.30097 * x) + min).dp(2, BigNumber.ROUND_HALF_UP)
  }

  private inverseCurve(x: BigNumber): number {
    const min = this.state.minValue.toNumber() - 0.3019
    return new BigNumber(Math.log((Number(x) - min) / 0.30194) / 2.30097)
      .dp(2, BigNumber.ROUND_HALF_UP)
      .toNumber()
  }

  public setLiquidationPrice = async () => {
    const liquidationPrice = this.state.maintenanceMargin
      .times(this.state.borrowAmount)
      .plus(this.state.borrowAmount)
      .div(this.state.depositAmount)

    this.setState({ liquidationPrice, isLoading: false })
  }

  public render() {
    const balanceTooLow = this.state.balanceValue.lt(this.state.depositAmount)
    const loanStatus = this.state.borrowAmount.eq(0)
      ? ''
      : this.state.minValue.plus(10).gt(this.state.collaterizationPercents)
      ? 'risky'
      : 'safe'

    const minSliderValue = this.inverseCurve(this.state.minValue)
    const maxSliderValue = this.inverseCurve(this.state.maxValue)
    const warningMessage = this.state.borrowAmount.gt(this.state.maxAvailableLiquidity)
      ? 'There is insufficient liquidity available for this loan'
      : balanceTooLow || this.state.balanceValue.isZero()
      ? `Insufficient ${this.state.collateralAsset} balance in your wallet!`
      : this.state.depositAmount.lt(0)
      ? 'Wrong deposit amount'
      : this.state.borrowAmount.lt(0)
      ? 'Wrong borrow amount'
      : this.state.borrowAmount.gt(0) && this.state.depositAmount.eq(0)
      ? `Loan is too large`
      : undefined

    const submitTitle = this.state.borrowAmount.toFixed(this._borrowDecimals, 1)
    const { spriteLogo } = assetUtils.getCommon(this.props.borrowAsset)
    const canSubmit =
      !warningMessage && this.state.depositAmount.gt(0) && this.state.borrowAmount.gt(0)
    const { nextBorrowInterestRate } = this.state

    return (
      <Form onCancel={this.props.onCancel} onSubmit={this.onSubmit}>
        <section className="form__left_block">
          <div className="form__info_block">
            <div className="form__info_block__logo">
              <Icon name={spriteLogo} size="auto" className="form__info_block__logo_svg" />
            </div>
            <div className="flex-col width-100p">
              <div className="txt-title txt-default form--asset">
                Borrow {this.props.borrowAsset}
              </div>
              <div className="borrow-flex--column-row">
                <div className="flex-row-center justify-between">
                  <div className="flex-col margin-top-1">
                    <div className="txt-alternate txt-muted margin-right-1ch">APR</div>
                    <div className="txt-default txt-subtitle">
                      <span title={this.props.interestRate.toFixed()}>
                        {formatPercent(this.props.interestRate)}%
                      </span>{' '}
                      {nextBorrowInterestRate && (
                        <span className="txt-positive" title={nextBorrowInterestRate.toFixed()}>
                          → {formatPercent(nextBorrowInterestRate)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <InfoTip id="borrow-apr-tooltip" top={-3}>
                    <div className="txt-explanation">
                      <p>This is the % of interest paid per year on your position.</p>
                      {nextBorrowInterestRate && nextBorrowInterestRate.gte(0.001) && (
                        <p>
                          <b>
                            With you current setup, the interest rates will raise to{' '}
                            {formatPercent(nextBorrowInterestRate)}% approximately.
                          </b>
                        </p>
                      )}
                      <p>
                        The rates are dynamic and depend on the pool utilization. Thanks to Ooki,
                        you will benefit from very low rates (almost free money) until the pool
                        reaches 80% of utilization.
                      </p>
                    </div>
                  </InfoTip>
                </div>
                <div className="flex-row-center justify-between">
                  <div className="flex-col margin-top-1">
                    <div className="txt-alternate txt-muted">Liquidity</div>
                    <div className="txt-default txt-subtitle">
                      {numberUtils.abbreviate(this.state.maxAvailableLiquidity)}{' '}
                      <span className="txt-alternate">{this.props.borrowAsset}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="form__content">
          <div className="flex-between">
            <div className="txt-subtitle margin-bottom-05">Choose your collateral</div>
            <div className="borrow-flex--row-column">
              <div className="txt-subtitle">
                <Button
                  variant="link"
                  className="btn--lowercase"
                  title={this.state.balanceValue.toFixed()}
                  onClick={this.setMaxBorrow}
                  style={{ cursor: 'pointer' }}>
                  {numberUtils.autoFormat(this.state.balanceValue)}{' '}
                </Button>{' '}
                <span className="txt-alternate txt-default margin-right-1ch">
                  {this.state.collateralAsset}
                </span>
                <Icon name="wallet-balance" size={20} top={-2} color="var(--color-txt-muted)" />
              </div>
            </div>
          </div>

          <div className="flex-row-center">
            <AssetDropdown
              className="margin-right-1"
              selectedAsset={this.state.collateralAsset}
              onAssetChange={this.onChangeCollateralAsset}
              assetsWithBalance={this.state.userBalances}
            />

            <Input
              className="flex-fill"
              onChange={this.onChangeDeposit}
              onChangeEmit="value"
              value={this.state.depositAmountValue}
            />
          </div>

          <div className="txt-subtitle margin-top-2 margin-bottom-05">
            Amount of {this.props.borrowAsset} you will borrow
          </div>

          <div className="input-with-sticked-btn">
            <Input
              disabled={!this.state.balanceValue.gt(0)}
              onChange={this.onBorrowAmountChange}
              onChangeEmit="value"
              value={this.state.borrowAmountValue}
            />
            <Button
              className="borrow-form__max-btn"
              variant="blue"
              onClick={this.setMaxBorrow}
              disabled={this.state.depositAmount.lte(0)}>
              Max
            </Button>
          </div>

          <div className="flex-between margin-bottom-1 margin-top-2">
            <span className="txt-negative txt-subtitle-super">Riskier</span>
            <span className="txt-positive txt-subtitle-super">Safer</span>
          </div>

          <Slider
            className="margin-bottom-1"
            type="colored"
            step={(maxSliderValue - minSliderValue) / 100}
            min={minSliderValue}
            max={maxSliderValue}
            value={this.state.sliderValue}
            onChange={this.onChange}
            onAfterChange={this._sliderChange}
          />

          <div className="margin-v-1">
            <ExpectedResult
              collaterizationPercents={this.state.collaterizationPercents}
              liquidationPrice={this.state.liquidationPrice}
              loanToken={this.props.borrowAsset}
              collateralToken={this.state.collateralAsset}
              loanStatus={loanStatus}
              chainId={fulcrumProvider.currentChainId}
            />
          </div>

          {warningMessage && !this.state.isLoading ? (
            <p className="form__warning">{warningMessage}</p>
          ) : this.state.needsApproval ? (
            <Button
              variant="green"
              className="block"
              isLoading={this.state.isLoading}
              onClick={this.approveSpending}>
              Approve spending
            </Button>
          ) : (
            <Button
              type="submit"
              variant="green"
              disabled={!canSubmit}
              isLoading={this.state.isLoading}
              title={submitTitle}>
              Borrow {numberUtils.autoFormat(this.state.borrowAmount)} {this.props.borrowAsset}
            </Button>
          )}

          {isNativeTokenForNetwork(fulcrumProvider.currentChainId, this.state.collateralAsset) && (
            <div className="txt-warning margin-top-1 txt-center">
              Make sure you keep enough {this.state.collateralAsset} gas token{' '}
              <InfoTip id="tip-gas-token">
                You are going to send {this.state.collateralAsset}, which is the gas token necessary
                to pay for your transactions. Make sure you always keep some.{' '}
              </InfoTip>
            </div>
          )}
        </section>
      </Form>
    )
  }

  private approveSpending = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const { spendingApprovals } = this.props.rootStore

    this.setState({ isLoading: true })
    const success = await spendingApprovals.setInfiniteApprovalForToken(
      this.state.collateralAsset,
      this.state.itokenAddress
    )
    if (success) {
      this.setNeedsApproval()
    }
    this.setState({ isLoading: false })
  }

  private onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const borrowRequest = new BorrowRequest(
      '0x0000000000000000000000000000000000000000000000000000000000000000',
      this.props.borrowAsset,
      this.state.borrowAmount,
      this.state.collateralAsset,
      this.state.depositAmount
    )

    this.props.onSubmit(borrowRequest)
  }

  private onChangeCollateralAsset = async (asset: Asset) => {
    if (this.state.collateralAsset !== asset) {
      const userBalance = this.state.userBalances.find((balance) => balance.token === asset)
      this.setState(
        {
          balanceValue: userBalance ? userBalance.amount : new BigNumber(NaN),
          depositAmount: userBalance ? userBalance.amount : new BigNumber(NaN),
          depositAmountValue: '',
          collateralAsset: asset,
          isLoading: true,
        },
        async () => {
          await this.setInputDefaults()
          await this.setMaxBorrow()
          this.setState({ isLoading: false })
        }
      )
    }
  }

  public onBorrowAmountChange = async (borrowAmountValue: string) => {
    const isValid = borrowAmountValue !== '' && _.isFinite(Number(borrowAmountValue))
    const stateUpdate: Partial<IBorrowFormState> = {
      borrowAmountValue,
    }
    if (isValid) {
      stateUpdate.borrowAmount = new BigNumber(borrowAmountValue)
      stateUpdate.isLoading = true
    }
    // @ts-ignore
    this.setState(stateUpdate, () => {
      if (isValid) {
        this._borrowAmountChange(this.state.borrowAmountValue)
      }
    })
  }

  public setMaxBorrow = async () => {
    if (this.state.balanceValue.eq(0)) {
      return
    }
    const depositAmountValue = numberUtils.formatPrecision(this.state.balanceValue)

    this.setState(
      {
        sliderValue: this.inverseCurve(this.state.minValue),
        collaterizationPercents: this.state.minValue,
        depositAmount: this.state.balanceValue,
        depositAmountValue,
        isLoading: true,
        liquidationPrice: new BigNumber(0),
      },
      () => {
        this._depositAmountChange(depositAmountValue)
      }
    )
  }

  private setDepositEstimate = (amount: BigNumber) => {
    const depositAmount = amount.times(this.state.collaterizationPercents).div(this.state.minValue)
    const precisionDigits = assetUtils.getDecimals(
      fulcrumProvider.currentChainId,
      this.state.collateralAsset
    )

    this.setState({
      depositAmount,
      depositAmountValue: numberUtils.formatPrecision(depositAmount.toFixed(precisionDigits, 1)),
    })
  }

  private onChange = async (sliderValue: number) => {
    this.setState({
      sliderValue,
      collaterizationPercents: this.sliderCurve(sliderValue),
      isLoading: true,
    })
  }

  public onChangeDeposit = async (depositAmountValue: string) => {
    const isValid = depositAmountValue !== '' && _.isFinite(Number(depositAmountValue))
    const stateUpdate: Partial<IBorrowFormState> = {
      depositAmountValue,
    }
    if (isValid) {
      stateUpdate.depositAmount = new BigNumber(depositAmountValue)
      stateUpdate.isLoading = true
    }
    // @ts-ignore
    this.setState(stateUpdate, () => {
      if (isValid) {
        this._depositAmountChange(depositAmountValue)
      }
    })
  }

  private borrowAmountChange = async (borrowAmount: string) => {
    const amountBN = new BigNumber(borrowAmount)
    const depositAmount = (
      await loanUtils.getDepositAmountEstimate(
        fulcrumProvider,
        this.props.borrowAsset,
        this.state.collateralAsset,
        amountBN
      )
    ).depositAmount

    this.setDepositEstimate(depositAmount)
    await this.setLiquidationPrice()
    this.refreshBorrowRate(amountBN)
    this.setNeedsApproval()
  }

  private depositAmountChange = async (depositAmount: string) => {
    try {
      const { borrowAmount } = await loanUtils.getBorrowAmountEstimate(
        fulcrumProvider,
        this.props.borrowAsset,
        this.state.collateralAsset,
        new BigNumber(depositAmount),
        this.sliderCurve(this.state.sliderValue),
        this.state.minValue.minus(0.3019)
      )

      this.setState({
        borrowAmount,
        borrowAmountValue: numberUtils.formatPrecision(
          borrowAmount.toFixed(this._borrowDecimals, 1)
        ),
      })

      await this.setLiquidationPrice()
      this.refreshBorrowRate(borrowAmount)
      this.setNeedsApproval()
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Could not estimate loan amount' })
      console.error(err)
      this.props.rootStore.setError(err)
    }
  }
}
